import React, { useState, useContext, useEffect } from 'react';
import { LeagueContext } from '../contexts/LeagueContext';
import { YearContext } from '../contexts/YearContext';
import { firestore } from '../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { FaCrown, FaUserShield, FaUser } from 'react-icons/fa6';

const LeagueSettings = ({ isOpen, onClose }) => {
  const { currentLeague, isLeagueAdmin, refreshLeagueData, leagueMembers } = useContext(LeagueContext);
  const { seasonInfo } = useContext(YearContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('general'); // 'general' or 'admins'
  
  // Form state
  const [settings, setSettings] = useState({
    predictionDeadline: '',
    maxMembers: 50,
    isPrivate: false,
    requiresApproval: false
  });

  // Load current league settings when component opens
  useEffect(() => {
    if (isOpen && currentLeague && currentLeague.settings) {
      // Convert predictionDeadline to datetime-local format
      let deadlineValue = '';
      if (currentLeague.settings.predictionDeadline) {
        try {
          const deadline = new Date(currentLeague.settings.predictionDeadline);
          // Format for datetime-local input: YYYY-MM-DDTHH:MM
          deadlineValue = deadline.toISOString().slice(0, 16);
        } catch (error) {
          console.warn('Error parsing predictionDeadline:', error);
          deadlineValue = '';
        }
      }

      setSettings({
        predictionDeadline: deadlineValue,
        maxMembers: currentLeague.settings.maxMembers || 50,
        isPrivate: currentLeague.settings.isPrivate || false,
        requiresApproval: currentLeague.settings.requiresApproval || false
      });
    }
  }, [isOpen, currentLeague]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate prediction deadline
      if (settings.predictionDeadline) {
        const deadline = new Date(settings.predictionDeadline);
        const now = new Date();
        
        // Check if deadline is in the future
        if (deadline <= now) {
          setError('Prediction deadline must be in the future');
          setLoading(false);
          return;
        }

        // Check if deadline is before season end (if season info available)
        if (seasonInfo && seasonInfo.endDate) {
          const seasonEnd = new Date(seasonInfo.endDate);
          if (deadline > seasonEnd) {
            setError('Prediction deadline must be before the season ends');
            setLoading(false);
            return;
          }
        }
      }

      // Update league settings in Firestore
      const leagueRef = doc(firestore, 'leagues', currentLeague.id);
      
      // Convert datetime-local back to ISO string, or set to empty string if no deadline
      const predictionDeadlineValue = settings.predictionDeadline 
        ? new Date(settings.predictionDeadline).toISOString()
        : '';
      
      await updateDoc(leagueRef, {
        'settings.predictionDeadline': predictionDeadlineValue,
        'settings.maxMembers': settings.maxMembers,
        'settings.isPrivate': settings.isPrivate,
        'settings.requiresApproval': settings.requiresApproval
      });
      

      setSuccess('League settings updated successfully!');
      
      // Close modal after success
      setTimeout(() => {
        onClose();
        setSuccess('');
        // Refresh league data after modal closes to avoid re-render issues
        refreshLeagueData();
      }, 1500);

    } catch (err) {
      console.error('Error updating league settings:', err);
      setError(err.message || 'Failed to update league settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToAdmin = async (memberUsername) => {
    if (!currentLeague || !memberUsername) return;

    try {
      setLoading(true);
      setError('');

      // Add username to admins array if not already there
      const currentAdmins = currentLeague.admins || [];
      if (!currentAdmins.includes(memberUsername)) {
        const leagueRef = doc(firestore, 'leagues', currentLeague.id);
        await updateDoc(leagueRef, {
          admins: [...currentAdmins, memberUsername]
        });

        setSuccess(`${memberUsername} promoted to admin successfully!`);
        
        // Refresh league data to update admin status
        setTimeout(() => {
          refreshLeagueData();
          setSuccess('');
        }, 2000);
      }
    } catch (err) {
      console.error('Error promoting member to admin:', err);
      setError(err.message || 'Failed to promote member to admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoteAdmin = async (adminUsername) => {
    if (!currentLeague || !adminUsername) return;

    try {
      setLoading(true);
      setError('');

      // Remove username from admins array
      const currentAdmins = currentLeague.admins || [];
      const updatedAdmins = currentAdmins.filter(admin => admin !== adminUsername);
      
      // Ensure at least one admin remains
      if (updatedAdmins.length === 0) {
        setError('Cannot remove the last admin. Promote someone else first.');
        return;
      }

      const leagueRef = doc(firestore, 'leagues', currentLeague.id);
      await updateDoc(leagueRef, {
        admins: updatedAdmins
      });

      setSuccess(`${adminUsername} demoted from admin successfully!`);
      
      // Refresh league data to update admin status
      setTimeout(() => {
        refreshLeagueData();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error demoting admin:', err);
      setError(err.message || 'Failed to demote admin');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if not a league admin
  if (!isLeagueAdmin) {
    return null;
  }

  if (!isOpen) return null;

  // Get min/max dates for deadline input
  const now = new Date();
  const minDate = new Date(now.getTime() + 60000).toISOString().slice(0, 16); // 1 minute from now
  const maxDate = seasonInfo && seasonInfo.endDate 
    ? new Date(seasonInfo.endDate).toISOString().slice(0, 16)
    : new Date(now.getFullYear() + 1, 5, 30).toISOString().slice(0, 16); // Default to end of next June

  const currentAdmins = currentLeague?.admins || [];
  const nonAdminMembers = leagueMembers.filter(member => 
    !currentAdmins.includes(member.username) && member.status === 'active'
  );

  return (
    <div className="modal modal-open">
      <div className="modal-box w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-2xl mb-6 flex items-center gap-2">
          <FaCrown className="text-primary" />
          League Settings - {currentLeague?.name}
        </h3>
        
        {/* Tabs */}
        <div className="tabs tabs-bordered mb-6">
          <button 
            className={`tab tab-bordered ${activeTab === 'general' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('general')}
            type="button"
          >
            <span className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              General Settings
            </span>
          </button>
          <button 
            className={`tab tab-bordered ${activeTab === 'admins' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('admins')}
            type="button"
          >
            <span className="flex items-center gap-2">
              <FaUserShield className="h-4 w-4" />
              Admin Management
            </span>
          </button>
        </div>
        
        {/* Error/Success Messages */}
        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}
        
        {success && (
          <div className="alert alert-success mb-4">
            <span>{success}</span>
          </div>
        )}

        {activeTab === 'general' ? (
          <form onSubmit={handleSaveSettings} className="space-y-6">
          {/* Prediction Deadline */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Prediction Deadline</span>
            </label>
            <input
              type="datetime-local"
              className="input input-bordered w-full"
              value={settings.predictionDeadline}
              onChange={(e) => setSettings({...settings, predictionDeadline: e.target.value})}
              min={minDate}
              max={maxDate}
            />
            <label className="label">
              <span className="label-text-alt">
                When predictions must be finalized. Defaults to season start date when league is created.
              </span>
            </label>
            {seasonInfo && (
              <label className="label">
                <span className="label-text-alt">
                  Current season: {new Date(seasonInfo.startDate).toLocaleDateString(undefined, { timeZone: 'UTC' })} - {new Date(seasonInfo.endDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                </span>
              </label>
            )}
          </div>

          {/* Max Members */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Maximum Members</span>
            </label>
            <input
              type="number"
              min="5"
              max="100"
              className="input input-bordered w-full"
              value={settings.maxMembers}
              onChange={(e) => setSettings({...settings, maxMembers: parseInt(e.target.value) || 50})}
            />
          </div>

          {/* Privacy Settings */}
          <div className="space-y-4">
            <h4 className="font-semibold">Privacy Settings</h4>
            
            <div className="form-control">
              <label className="cursor-pointer label">
                <span className="label-text">Private League</span>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={settings.isPrivate}
                  onChange={(e) => setSettings({...settings, isPrivate: e.target.checked})}
                />
              </label>
              <label className="label">
                <span className="label-text-alt">Private leagues require invite codes to join</span>
              </label>
            </div>

            <div className="form-control">
              <label className="cursor-pointer label">
                <span className="label-text">Require Admin Approval</span>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={settings.requiresApproval}
                  onChange={(e) => setSettings({...settings, requiresApproval: e.target.checked})}
                />
              </label>
              <label className="label">
                <span className="label-text-alt">New members must be approved by an admin</span>
              </label>
            </div>
          </div>

          {/* Current League Info */}
          <div className="bg-base-200 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Current League Info</h4>
            <div className="text-sm space-y-1">
              <p><strong>League ID:</strong> {currentLeague?.id}</p>
              <p><strong>Members:</strong> {currentLeague?.memberCount || 0}</p>
              <p><strong>Created:</strong> {currentLeague?.createdAt ? new Date(currentLeague.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}</p>
              {currentLeague?.settings?.inviteCode && (
                <p><strong>Invite Code:</strong> <code className="bg-base-300 px-2 py-1 rounded">{currentLeague.settings.inviteCode}</code></p>
              )}
            </div>
          </div>

          <div className="modal-action">
            <button 
              type="button" 
              className="btn btn-ghost" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={`btn btn-primary ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
        ) : (
          <div className="space-y-6">
            {/* Current Admins */}
            <div className="space-y-4">
              <h4 className="font-semibold text-lg">Current Admins ({currentAdmins.length})</h4>
              
              {currentAdmins.length > 0 ? (
                <div className="space-y-2">
                  {currentAdmins.map((adminUsername) => {
                    const memberData = leagueMembers.find(member => member.username === adminUsername);
                    return (
                      <div key={adminUsername} className="flex items-center justify-between bg-base-200 p-4 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FaCrown className="text-yellow-500" />
                          <div>
                            <div className="font-medium">{adminUsername}</div>
                            {memberData && (
                              <div className="text-sm text-base-content/70">
                                {memberData.email}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {currentAdmins.length > 1 && (
                          <button
                            className="btn btn-sm btn-error btn-outline"
                            onClick={() => handleDemoteAdmin(adminUsername)}
                            disabled={loading}
                          >
                            Demote
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-base-content/70 py-4">
                  No admins found
                </div>
              )}
            </div>

            {/* Promote Members to Admin */}
            {nonAdminMembers.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-semibold text-lg">Promote Members to Admin</h4>
                
                <div className="space-y-2">
                  {nonAdminMembers.map((member) => (
                    <div key={member.username} className="flex items-center justify-between bg-base-200 p-4 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FaUser className="text-base-content/70" />
                        <div>
                          <div className="font-medium">{member.username}</div>
                          <div className="text-sm text-base-content/70">
                            {member.email}
                          </div>
                        </div>
                      </div>
                      
                      <button
                        className="btn btn-sm btn-primary btn-outline"
                        onClick={() => handlePromoteToAdmin(member.username)}
                        disabled={loading}
                      >
                        <FaUserShield className="mr-1" />
                        Promote to Admin
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {nonAdminMembers.length === 0 && (
              <div className="bg-info/10 border border-info/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-info">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">All active members are already admins</span>
                </div>
              </div>
            )}
            
            {/* Admin Management Info */}
            <div className="bg-base-200 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Admin Permissions</h4>
              <ul className="text-sm space-y-1 text-base-content/80">
                <li>• Manage league settings and privacy options</li>
                <li>• Promote and demote other members</li>
                <li>• Access admin-only features and statistics</li>
                <li>• At least one admin must remain in the league</li>
              </ul>
            </div>

            <div className="modal-action">
              <button 
                type="button" 
                className="btn btn-ghost" 
                onClick={onClose}
                disabled={loading}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueSettings;